import {
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  TextField,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  AccordionDetails,
  AccordionSummary,
  Accordion,
} from "@mui/material";
import RouteIcon from "@mui/icons-material/Route";
import PersonIcon from "@mui/icons-material/Person";
import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import NavigationHeader from "../../../components/NavigationHeader";
import { PROJECT_TERMINOLOGY, ProjectTabEnum } from "../enums";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
  tableHeaderStyles,
  tableCellStyles,
} from "../../../utils/styles";
import { useToast } from "../../../components/Toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { projectLogApiService } from "../services/projectLogApiService";
import { Loading } from "../../../components/Loading";
import InfoIcon from "@mui/icons-material/Info";
import { UploadedFile } from "../../../components/FileUploadSection";
import { DocumentUploadSection } from "../../../components/DocumentUploadSection";
import { uploadFile } from "../../../utils/UploadFile";
import { formatText, formatVehicleNumber } from "../../../utils/utils";
import { renderBasicLogInfo } from "./ProjectLogs";
import { renderBasicLogInfoByVehicle } from "./ProjectLogsByVehicle";
import { OperationType } from "../../../types/operationType";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import { ProjectDetailTabs } from "../types";
import ExpandMore from "@mui/icons-material/ExpandMore";

// Interface for the project log data structure
interface ProjectLogData {
  id: string;
  companyId: string;
  createdBy: string;
  projectId: string;
  routeId: string;
  scheduleId: string | null;
  vehicleId: string;
  startLocation: string;
  endLocation: string;
  startTime: string;
  endTime: string;
  startOdometer: number | null;
  endOdometer: number | null;
  dateOfEntry: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  occupancy: number;
  notes?: string;
  staffAttendances: Array<{
    id: string;
    projectLogId: string;
    staffId: string;
    attendanceStatus: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  }>;
  vehicle: {
    id: string;
    status: string;
    companyId: string;
    vehicleType: string;
    acquisitionType: string;
    vehicleNumber: string;
    chassisNumber: string;
    engineNumber: string | null;
    makeYear: number;
    model: string;
    color: string;
    bodyType: string;
    configuration: string;
    fuelType: string;
    seatingCapacity: number;
    isAC: boolean;
    firstServiceDue: string | null;
    maintenanceInterval: string;
    nextMaintenanceDate: string | null;
    warrantyPeriod: string | null;
    lastServiceDate: string | null;
    lastServiceOdometer: number | null;
    knownMaintenanceIssues: string | null;
    vehicleCost: number | null;
    isFinanced: boolean;
    insuranceType: string | null;
    insuranceProvider: string | null;
    insurancePolicyNumber: string | null;
    insuranceStartDate: string | null;
    insuranceEndDate: string | null;
    insurancePremium: number | null;
    fastagNumber: string | null;
    fastagProvider: string | null;
    rcNumber: string | null;
    rcExpiryDate: string;
    pucNumber: string | null;
    pucStartDate: string | null;
    pucEndDate: string | null;
    fitnessExpiryDate: string | null;
    pollutionExpiryDate: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  schedule: any | null;
  route: {
    id: string;
    fromLocation: string;
    toLocation: string;
    routeManagerName: string;
    routeManagerNumber: string;
    companyId: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
}

// Interface for staff details
interface StaffDetails {
  id: string;
  name: string;
  contactNumber: string;
  type: string;
  status: string;
  employmentType: string;
  address: string;
  drivingLicense?: string;
  photoUrl?: string;
  alternateNumber?: string;
  joiningDate?: string;
  salary?: number;
  aadharNumber?: string;
  referredByName?: string;
  referredByContact?: string;
  reference1Name?: string;
  reference1Contact?: string;
  reference2Name?: string;
  reference2Contact?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

const EditProjectLog = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const { id: projectId, superProjectId } = useParams();
  const localUser = sessionStorage.getItem("user");
  const user = localUser ? JSON.parse(localUser) : null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logId = searchParams.get("logId");
  const isDivision = searchParams.get("isDivision") === "true";
  const [projectLog, setProjectLog] = useState<ProjectLogData | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const { planStatus } = usePlanStatus();
  const [addNotes, setAddNotes] = useState(false);
  const fetchProjectLog = async () => {
    if (logId) {
      setIsLoading(true);
      try {
        const log = await projectLogApiService.getProjectLogById(logId);
        setProjectLog(log);
        setUploadedFiles(log.documents || []);
      } catch (error) {
        console.error("Error fetching project log:", error);
        showToast();
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProjectLog();
  }, [logId]);

  // Create validation schema based on actual staff data
  const createValidationSchema = (projectLog: ProjectLogData) => {
    const staffDetails = projectLog.staffAttendances
      .map((attendance: any) => attendance.staff)
      .filter(Boolean);

    return Yup.object().shape({
      staffAttendances: Yup.object().shape(
        staffDetails.reduce((acc: any, staff: any) => {
          acc[staff.id] = Yup.string()
            .oneOf(["PRESENT", "ABSENT"], "Please select attendance status")
            .required("Attendance status is required");
          return acc;
        }, {} as { [key: string]: Yup.StringSchema })
      ),
      startTime: Yup.string().required("Start time is required"),
      endTime: Yup.string()
        .required("End time is required")
        .test(
          "end-time-after-start-time",
          "End time must be after start time",
          (value, context) => {
            const startTime = context.parent.startTime;
            if (!startTime) return true;
            const [startHours, startMinutes] = startTime.split(":").map(Number);
            const [endHours, endMinutes] = value.split(":").map(Number);
            return (
              endHours > startHours ||
              (endHours === startHours && endMinutes > startMinutes)
            );
          }
        ),
      startOdometer: Yup.number()
        .typeError("Start odometer must be a number")
        .positive("Start odometer must be positive")
        .optional(),
      endOdometer: Yup.number()
        .typeError("End odometer must be a number")
        .positive("End odometer must be positive")
        .test(
          "end-odometer-after-start-odometer",
          "End odometer must be greater than start odometer",
          (value, context) => {
            const startOdometer = context.parent.startOdometer;
            if (!startOdometer) return true;
            return !value || value > startOdometer;
          }
        )
        .optional(),
      dateOfEntry: Yup.date().required("Date of entry is required"),
      capacity: Yup.number().optional(),
      occupancy: Yup.number()
        .required("Occupancy is required")
        .min(0, "Occupancy cannot be negative")
        .test(
          "occupancy-less-than-capacity",
          "Occupancy must be less than vehicle capacity",
          (value, context) => {
            const capacity = context.parent.capacity;
            if (!capacity) return true;
            return !value || value <= capacity;
          }
        ),
    });
  };

  // Create initial values based on actual project log data
  const createInitialValues = (projectLog: ProjectLogData) => {
    const staffDetails = projectLog.staffAttendances
      .map((attendance: any) => attendance.staff)
      .filter(Boolean);

    const staffAttendances = staffDetails.reduce((acc: any, staff: any) => {
      const attendance = projectLog.staffAttendances.find(
        (att) => att.staffId === staff.id
      );
      acc[staff.id] = attendance?.attendanceStatus || "PRESENT";
      return acc;
    }, {} as { [key: string]: string });

    return {
      staffAttendances,
      startTime: projectLog.startTime || "",
      endTime: projectLog.endTime || "",
      startOdometer: projectLog.startOdometer || "",
      endOdometer: projectLog.endOdometer || "",

      dateOfEntry: projectLog.dateOfEntry
        ? new Date(projectLog.dateOfEntry).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      companyId: user?.companyId,
      createdBy: user?.id,
      projectId,
      routeId: projectLog.routeId,
      scheduleId: projectLog.scheduleId,
      vehicleId: projectLog.vehicleId,
      capacity: projectLog.vehicle?.seatingCapacity || 0,
      occupancy: projectLog.occupancy || 0, // This should come from the API if available
      notes: projectLog.notes || "",
    };
  };

  const handleSubmit = async (
    values: any,
    { setSubmitting, resetForm }: any
  ) => {
    try {
      const response = await projectLogApiService.updateProjectLog(
        logId!,
        values
      );
      if (selectedFiles && selectedFiles?.length > 0) {
        const uploadPromises = selectedFiles.map((file: File) =>
          uploadFile({
            file: file,
            documentType: "project-log",
            documentTypeId: logId! || response?.id,
            documentContext: "project-log",
            description: "Project log",
          })
        );
        await Promise.all(uploadPromises);
      }

      showToast("Log updated successfully", "success");
      navigate(-1);
    } catch (error) {
      console.error("Error submitting log:", error);
      showToast("Failed to submit log. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loading message="Loading project log..." />;
  }

  if (!projectLog) {
    return (
      <Container sx={{ p: 1, my: 3 }}>
        <NavigationHeader
          title={"Edit Log"}
          breadcrumbs={[
            {
              label: PROJECT_TERMINOLOGY.PLURAL,
              path: superProjectId
                ? `/super`
                : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
            },
            ...(superProjectId
              ? [
                  {
                    label: ProjectTabEnum.DIVISIONS.label,
                    path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                  },
                  {
                    label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                    path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                  },
                ]
              : []),
            {
              label: ProjectTabEnum.LOGS.label,
              path: superProjectId
                ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`
                : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`,
            },
            {
              label: "Edit Log",
            },
          ]}
        />
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Project log not found
          </Typography>
        </Box>
      </Container>
    );
  }

  const validationSchema = createValidationSchema(projectLog);
  const initialValues = createInitialValues(projectLog);

  return (
    <Container sx={{ p: 1 }}>
      <NavigationHeader
        title={"Edit Log"}
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: superProjectId
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          ...(superProjectId
            ? [
                {
                  label: ProjectTabEnum.DIVISIONS.label,
                  path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                },
                {
                  label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                  path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                },
              ]
            : []),
          {
            label: ProjectTabEnum.LOGS.label,
            path: superProjectId
              ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`,
          },
          {
            label: "Edit Log",
          },
        ]}
      />

      <Box sx={{ mb: 1 }}>
        {planStatus?.operationType === OperationType.LARGE_FLEET
          ? renderBasicLogInfoByVehicle(projectLog?.vehicle)
          : renderBasicLogInfo(
              projectLog?.route?.fromLocation,
              projectLog?.route?.toLocation,
              projectLog?.vehicle?.vehicleNumber
            )}
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, isSubmitting, isValid }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ px: 2, py: 1 }}>
                    <Grid container spacing={2}>
                      {/* Required Fields */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item container md={6} spacing={2}>
                              <Grid item xs={12}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <RouteIcon
                                    color="primary"
                                    sx={{ fontSize: "0.75rem" }}
                                  />
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Trip Information
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Field
                                  name="dateOfEntry"
                                  as={TextField}
                                  label="Date of Entry"
                                  type="date"
                                  fullWidth
                                  {...commonDateFieldProps}
                                />
                              </Grid>
                              <Grid item xs={6} sm={6} md={3}>
                                <Field
                                  name="startTime"
                                  as={TextField}
                                  label="Start Time"
                                  type="time"
                                  fullWidth
                                  error={
                                    !!errors.startTime && touched.startTime
                                  }
                                  helperText={
                                    errors.startTime && touched.startTime
                                      ? errors.startTime
                                      : ""
                                  }
                                  {...commonDateFieldProps}
                                  required
                                />
                              </Grid>
                              <Grid item xs={6} sm={6} md={3}>
                                <Field
                                  name="endTime"
                                  as={TextField}
                                  label="End Time"
                                  type="time"
                                  fullWidth
                                  error={!!errors.endTime && touched.endTime}
                                  helperText={
                                    errors.endTime && touched.endTime
                                      ? errors.endTime
                                      : ""
                                  }
                                  {...commonDateFieldProps}
                                  required
                                />
                              </Grid>
                            </Grid>
                            <Grid item container md={6} spacing={2}>
                              <Grid item xs={12}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <InfoIcon
                                    color="primary"
                                    sx={{ fontSize: "0.75rem" }}
                                  />
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Optional Information
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={6} md={3}>
                                <Field
                                  name="startOdometer"
                                  as={TextField}
                                  label="Start KMs"
                                  type="number"
                                  fullWidth
                                  inputProps={{
                                    min: 0,
                                    step: 1,
                                    onWheel: (
                                      e: React.WheelEvent<HTMLInputElement>
                                    ) => (e.target as HTMLInputElement).blur(),
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          KMs
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  size={commonTextFieldProps.size}
                                  sx={{
                                    ...commonTextFieldProps.sx,
                                    "& input[type=number]": {
                                      MozAppearance: "textfield",
                                    },
                                    "& input[type=number]::-webkit-outer-spin-button":
                                      {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                    "& input[type=number]::-webkit-inner-spin-button":
                                      {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6} sm={6} md={3}>
                                <Field
                                  name="endOdometer"
                                  as={TextField}
                                  label="End KMs"
                                  type="number"
                                  fullWidth
                                  inputProps={{
                                    min: 0,
                                    step: 1,
                                    onWheel: (
                                      e: React.WheelEvent<HTMLInputElement>
                                    ) => (e.target as HTMLInputElement).blur(),
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          KMs
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  size={commonTextFieldProps.size}
                                  sx={{
                                    ...commonTextFieldProps.sx,
                                    "& input[type=number]": {
                                      MozAppearance: "textfield",
                                    },
                                    "& input[type=number]::-webkit-outer-spin-button":
                                      {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                    "& input[type=number]::-webkit-inner-spin-button":
                                      {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                  }}
                                />
                              </Grid>
                              {!isDivision && (
                                <Grid item xs={12} sm={6} md={3}>
                                  <Field
                                    name="occupancy"
                                    as={TextField}
                                    label="Occupancy"
                                    type="number"
                                    fullWidth
                                    inputProps={{
                                      min: 0,
                                      step: 1,
                                      onWheel: (
                                        e: React.WheelEvent<HTMLInputElement>
                                      ) =>
                                        (e.target as HTMLInputElement).blur(),
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Typography variant="body2">
                                            {values.capacity - values.occupancy}
                                          </Typography>
                                        </InputAdornment>
                                      ),
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                    error={
                                      !!errors.occupancy && touched.occupancy
                                    }
                                    helperText={
                                      errors.occupancy && touched.occupancy
                                        ? errors.occupancy
                                        : ""
                                    }
                                    size={commonTextFieldProps.size}
                                    sx={{
                                      ...commonTextFieldProps.sx,
                                      "& input[type=number]": {
                                        MozAppearance: "textfield",
                                      },
                                      "& input[type=number]::-webkit-outer-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                      "& input[type=number]::-webkit-inner-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                    }}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                      {/* Staff Attendance Section */}
                      {projectLog.staffAttendances.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              borderRadius: 2,
                              bgcolor: "background.paper",
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={2}
                            >
                              <PersonIcon
                                color="primary"
                                sx={{ fontSize: "1rem" }}
                              />
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                              >
                                Staff Attendance
                              </Typography>
                            </Box>
                            <TableContainer
                              component={Paper}
                              elevation={0}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                              }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: "grey.50" }}>
                                    <TableCell sx={tableHeaderStyles}>
                                      Staff
                                    </TableCell>
                                    <TableCell sx={tableHeaderStyles}>
                                      Attendance
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {projectLog.staffAttendances
                                    .map((attendance: any) => attendance.staff)
                                    .filter(Boolean)
                                    .map((staff: any) => (
                                      <TableRow
                                        key={staff.id}
                                        sx={{
                                          "&:hover": { bgcolor: "grey.50" },
                                        }}
                                      >
                                        <TableCell sx={tableCellStyles}>
                                          {staff.name} <br />(
                                          {formatText(staff.type)}
                                          {`${
                                            staff.contactNumber
                                              ? ` / ${staff.contactNumber}`
                                              : ""
                                          }`}
                                          )
                                        </TableCell>
                                        <TableCell sx={tableCellStyles}>
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                          >
                                            {values.staffAttendances[
                                              staff.id
                                            ] === "PRESENT"
                                              ? "Present"
                                              : "Absent"}
                                            <Switch
                                              checked={
                                                values.staffAttendances[
                                                  staff.id
                                                ] === "PRESENT"
                                              }
                                              onChange={(e) => {
                                                const value = e.target.checked
                                                  ? "PRESENT"
                                                  : "ABSENT";
                                                handleChange({
                                                  target: {
                                                    name: `staffAttendances.${staff.id}`,
                                                    value: value,
                                                  },
                                                });
                                              }}
                                              color="success"
                                              size="small"
                                            />
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          onClick={() => setAddNotes(!addNotes)}
                          size={smallButtonProp.size}
                          sx={{
                            ...smallButtonProp.sx,
                            color: "grey",
                            borderColor: "grey.400",
                            "&:hover": {
                              borderColor: "grey.400",
                              color: "grey",
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {addNotes ? "Hide Notes" : "Add Notes"}
                          </Typography>
                        </Button>
                      </Grid>
                      {addNotes && (
                        <Grid item xs={12}>
                          <Field
                            name="notes"
                            as={TextField}
                            label="Notes"
                            fullWidth
                            multiline
                            rows={2}
                            {...commonTextFieldProps}
                          />
                        </Grid>
                      )}
                      {/* Upload Section */}
                      <Grid item xs={12}>
                        <Accordion
                          sx={{
                            border: "none",
                            elevation: 0,
                            boxShadow: "none",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls="expense-proofs-content"
                            id="expense-proofs-header"
                            sx={{
                              p: 0,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                            >
                              Upload Files
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <DocumentUploadSection
                              title=""
                              selectedFiles={selectedFiles}
                              uploadedFiles={uploadedFiles}
                              onFileSelect={(files: File[]) =>
                                setSelectedFiles((prev) => [...prev, ...files])
                              }
                              onFileRemove={(index: number) =>
                                setSelectedFiles((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                              onPreview={(url) => {
                                window.open(url, "_blank");
                              }}
                              isLoading={isLoading}
                            />
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    </Grid>

                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      gap={2}
                      mt={2}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        color="error"
                        onClick={() => navigate(-1)}
                        {...smallButtonProp}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        {...smallButtonProp}
                      >
                        {isSubmitting ? "Updating..." : "Update Log"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default EditProjectLog;
